import { useCallback, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    DOC_PROCESS_RUNSHEET_STATUSES,
    DOCUMENT_STATUS_SAVED,
    TITLE_CHAIN_CONVEYANCE_TYPES,
    LEASE_CHAIN_CONVEYANCE_TYPES,
    TAB_TYPE_DEED,
} from '@constants'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { conveyanceRound, sumConveyance } from '../ConveyanceUtils'
import './ConveyanceTable.css'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const ConveyanceTableViewModel = ({
    chain,
    setChain,
    conveyanceType,
    runsheetDocs,
    activeTab,
}) => {
    
    const { id: runsheetId } = useParams()
    const navigate = useNavigate()
    
    const titleRef = useRef()
    const [showAdd, setShowAdd] = useState(false)
    const [editRsDocJoin, setEditRsDocJoin] = useState()
    const [showFlowchart, setShowFlowchart] = useState(false)
    const [selectedParty, setSelectedParty] = useState(null)
    
    const uniqueParties = useWireValue(store.uniqueParties)
    const tabs = useWireValue(store.runsheetTabs)
    
    const runsheet = useWireValue(store.currentRunsheet)
    const remoteConfig = useWireValue(store.remoteConfig)
    
    const chainHeaders = useMemo(() => (
        conveyanceType === TAB_TYPE_DEED
            ? TITLE_CHAIN_CONVEYANCE_TYPES : LEASE_CHAIN_CONVEYANCE_TYPES
    ), [conveyanceType])
    
    const documents = useMemo(() => chain?.documents || [], [chain])
    
    const documentsToProcess = useMemo(() => runsheetDocs
        .map(rdj => rdj.document)
        .filter(doc =>
            (doc.reviewStatus === DOCUMENT_STATUS_SAVED && !doc.orgStatus)
            || DOC_PROCESS_RUNSHEET_STATUSES.includes(doc.orgStatus),
        )
        .map(d => d.id) || [], [runsheetDocs])
    
    const parties = useMemo(() => {
        
        const partyData = documents.reduce((acc, curr) => {
            acc.push(
                ...curr.value.grantors.map(g => ({ ...g, partyType: 'grantor' })),
                ...curr.value.grantees.map(g => ({ ...g, partyType: 'grantee' })),
                ...(curr.value.grantorGrantee?.map(g => ({ ...g, partyType: 'grantor_grantee' })) || []),
            )
            return acc
        }, [])
        
        return Object.values(partyData.reduce((acc, curr) => {
            const party = acc[curr.id] = acc[curr.id] || {
                id: curr.id,
                name: curr.name,
                conveyances: [],
            }
            
            party.conveyances.push({ ...curr.conveyance, partyType: curr.partyType })
            return acc
        }, {}))
        
    }, [documents])
    
    const getTooltipContent = () => {
        
        if (documentsToProcess.length > 0)
            return 'All documents must be processed to use Auto-chain feature.'
        
        if (chain?.status === 'stale')
            return 'The runsheet has been updated. For improved results, please run Auto-chain again.'
        
        if (chain?.status === 'processing')
            return `${conveyanceType === TAB_TYPE_DEED
                ? 'Title'
                : 'Leasehold'} chain is processing. It should be ready soon.`
        
        return ''
        
    }
    
    const updateDocData = async () => {
        
        const data = await actions.fetchTitleBreakdown(runsheetId, activeTab)
        
        setChain(data)
        
    }
    
    const getTractLink = titleBreakdown => `/tract/${titleBreakdown.tractId}/ownership` +
        `?runsheetId=${titleBreakdown.runsheetId}&runsheetTabId=${titleBreakdown.runsheetTabId}`
    
    const getPartyMergeCount = useCallback(partyId => (
        uniqueParties?.find(up => up.id === partyId)?.parties.length || 1
    ), [uniqueParties])
    
    const sumConveyances = (conveyances, field) => conveyanceRound(
        conveyances.reduce(sumConveyance(field), 0))
    
    const getTotalConveyances = field => conveyanceRound(parties.reduce((acc, curr) => {
        
        return acc + curr.conveyances
            .reduce(sumConveyance(field), 0)
        
    }, 0))
    
    const handleDeleteTitleChain = async () => {
        
        // @todo real dialog
        if (!confirm('Are you sure you want to delete this title chain?'))
            return
        
        try {
            
            await actions.deleteTitleChain(chain.runsheetId)
            
            toast.success('Title chain deleted successfully.')
            navigate(`/runsheet/${chain.runsheetId}`)
            
        } catch (e) {
            
            console.error('handleDeleteTitleChain', e)
            toast.error('Failed to delete title chain')
            
        }
        
    }
    
    return {
        
        // Params
        runsheetId,
        
        // Refs
        titleRef,
        
        // State
        showAdd,
        setShowAdd,
        editRsDocJoin,
        setEditRsDocJoin,
        showFlowchart,
        setShowFlowchart,
        selectedParty,
        setSelectedParty,
        
        // Global State
        uniqueParties,
        tabs,
        runsheet,
        remoteConfig,
        
        // Hooks
        navigate,
        
        // Memos
        chainHeaders,
        documents,
        documentsToProcess,
        parties,
        
        // Methods
        getTooltipContent,
        updateDocData,
        getTractLink,
        getPartyMergeCount,
        sumConveyances,
        getTotalConveyances,
        handleDeleteTitleChain,
        
    }
    
}

export default ConveyanceTableViewModel
